/**
 * skaile.yaml v2 extensions + skaile.lock.yaml reader/writer.
 *
 * The v2 workspace format extends the existing v1 skaile.yaml with:
 *   - `version: 2` marker
 *   - `library:` section (default pin policy, instance refs with aliases)
 *   - `presets_applied:` section (applied Preset references with timestamps)
 *
 * The lock file (`skaile.lock.yaml`) is new in v2 and records resolved
 * assignment state (pinned versions, SHA256 hashes, requires graph).
 *
 * @docLink packages/library/api-reference#workspace-config
 */

import * as fs from "node:fs";
import * as path from "node:path";
import { parse as yamlParse, stringify as yamlStringify } from "yaml";
import * as z from "zod";
import { PIN_POLICIES } from "./library.js";

// ---------------------------------------------------------------------------
// skaile.yaml v2 — library section schemas
// ---------------------------------------------------------------------------

/**
 * Zod schema for the `PinPolicy` type.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export const PinPolicySchema = z.enum(PIN_POLICIES);

/**
 * Zod schema for a single instance reference entry within `library.instances`.
 *
 * Each entry holds the instance UUID and an optional human-readable alias.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export const LibraryInstanceRefSchema = z.object({
  id: z.string().uuid(),
  alias: z.string().optional(),
});

/**
 * Zod schema for the `library:` section of skaile.yaml v2.
 *
 * Defaults: `pin_policy = "minor-track"`, `instances = []`.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export const LibrarySectionSchema = z.object({
  pin_policy: PinPolicySchema.default("minor-track"),
  instances: z.array(LibraryInstanceRefSchema).default([]),
});

/**
 * Zod schema for a single item entry inside a `presets_applied` record.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
const PresetAppliedItemSchema = z.object({
  instance_id: z.string().uuid(),
  def_ref: z.string(),
});

/**
 * Zod schema for a single applied-preset entry in the `presets_applied:` section.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export const PresetAppliedSchema = z.object({
  ref: z.string(),
  applied_at: z.string(),
  items: z.array(PresetAppliedItemSchema).default([]),
});

/**
 * Zod schema for the full skaile.yaml v2 workspace config document.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export const WorkspaceConfigV2Schema = z.object({
  version: z.literal(2),
  library: LibrarySectionSchema.optional(),
  presets_applied: z.array(PresetAppliedSchema).optional(),
});

// ---------------------------------------------------------------------------
// skaile.lock.yaml schemas
// ---------------------------------------------------------------------------

/**
 * Zod schema for a single resolved-assignment entry in skaile.lock.yaml.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export const LockAssignmentSchema = z.object({
  assignment_id: z.string().uuid(),
  instance_id: z.string().uuid(),
  def_ref: z.string(),
  resolved_version: z.string(),
  resolved_sha256: z.string().optional(),
  pin_policy: PinPolicySchema,
});

/**
 * Zod schema for a single directed edge in the asset requires graph.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export const RequiresEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  kind: z.string().optional(),
});

/**
 * Zod schema for a single expanded-preset entry tracking which instance IDs
 * a preset application produced.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export const PresetExpandedSchema = z.object({
  preset_ref: z.string(),
  instance_ids: z.array(z.string().uuid()),
});

/**
 * Zod schema for the complete skaile.lock.yaml document.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export const LockFileSchema = z.object({
  version: z.literal(2),
  generated_at: z.string(),
  assignments: z.array(LockAssignmentSchema).default([]),
  requires_graph: z.array(RequiresEdgeSchema).default([]),
  presets_expanded: z.array(PresetExpandedSchema).default([]),
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * TypeScript type for the `library:` section of skaile.yaml v2.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export type LibrarySection = z.infer<typeof LibrarySectionSchema>;

/**
 * A single instance UUID reference with optional alias in `library.instances`.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export type LibraryInstanceRef = z.infer<typeof LibraryInstanceRefSchema>;

/**
 * Record of a previously applied preset in the `presets_applied:` section.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export type PresetApplied = z.infer<typeof PresetAppliedSchema>;

/**
 * The parsed v2 workspace config (skaile.yaml `version: 2` document).
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export type WorkspaceConfigV2 = z.infer<typeof WorkspaceConfigV2Schema>;

/**
 * A single resolved-assignment record from skaile.lock.yaml.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export type LockAssignment = z.infer<typeof LockAssignmentSchema>;

/**
 * The parsed skaile.lock.yaml document.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export type LockFile = z.infer<typeof LockFileSchema>;

// ---------------------------------------------------------------------------
// skaile.yaml v2 reader/writer
// ---------------------------------------------------------------------------

/**
 * Extract the v2-specific sections from a parsed skaile.yaml object.
 * Returns null if the object is v1 (no `version: 2` marker).
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export function parseWorkspaceV2(raw: Record<string, unknown>): WorkspaceConfigV2 | null {
  if (raw.version !== 2) return null;
  return WorkspaceConfigV2Schema.parse(raw);
}

/**
 * Read the v2 sections from a skaile.yaml file on disk.
 * Returns null if the file doesn't exist or is v1.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export function loadWorkspaceV2(dir: string, filename = "skaile.yaml"): WorkspaceConfigV2 | null {
  const filePath = path.join(dir, filename);
  if (!fs.existsSync(filePath)) return null;
  const content = fs.readFileSync(filePath, "utf-8");
  const raw = yamlParse(content) as Record<string, unknown>;
  if (!raw || typeof raw !== "object") return null;
  return parseWorkspaceV2(raw);
}

/**
 * Write v2 sections into an existing skaile.yaml, preserving other content.
 * Creates a minimal v2 file if none exists.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export function saveWorkspaceV2(
  dir: string,
  v2: Partial<Omit<WorkspaceConfigV2, "version">>,
  filename = "skaile.yaml",
): void {
  const filePath = path.join(dir, filename);
  let existing: Record<string, unknown> = {};
  if (fs.existsSync(filePath)) {
    const content = fs.readFileSync(filePath, "utf-8");
    existing = (yamlParse(content) as Record<string, unknown>) ?? {};
  }
  existing.version = 2;
  if (v2.library !== undefined) existing.library = v2.library;
  if (v2.presets_applied !== undefined) existing.presets_applied = v2.presets_applied;

  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
  fs.writeFileSync(filePath, yamlStringify(existing, { indent: 2 }), "utf-8");
}

// ---------------------------------------------------------------------------
// skaile.lock.yaml reader/writer
// ---------------------------------------------------------------------------

const LOCK_FILENAME = "skaile.lock.yaml";

/**
 * Load and validate a skaile.lock.yaml file.
 * Returns null if the file doesn't exist.
 *
 * Back-compat: read legacy `subscriptions:` lock key written before the
 * 2026-06-08 assignment rename. Normalises to `assignments:` before parse.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export function loadLockFile(dir: string): LockFile | null {
  const filePath = path.join(dir, LOCK_FILENAME);
  if (!fs.existsSync(filePath)) return null;
  const content = fs.readFileSync(filePath, "utf-8");
  const raw = yamlParse(content) as Record<string, unknown>;
  // Pre-2026-06-08 lock files used `subscriptions`/`subscription_id`; Zod
  // rejects them without this shim, so normalise to the new keys before parse.
  if (raw !== null && !("assignments" in raw) && "subscriptions" in raw) {
    const legacySubs = raw.subscriptions;
    raw.assignments = Array.isArray(legacySubs)
      ? legacySubs.map((s) => {
          if (
            s !== null &&
            typeof s === "object" &&
            "subscription_id" in s &&
            !("assignment_id" in s)
          ) {
            const { subscription_id, ...rest } = s as Record<string, unknown>;
            return { assignment_id: subscription_id, ...rest };
          }
          return s;
        })
      : [];
    delete raw.subscriptions;
  }
  return LockFileSchema.parse(raw);
}

/**
 * Write a skaile.lock.yaml file. Overwrites any existing lock file.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export function saveLockFile(dir: string, lock: LockFile): void {
  const filePath = path.join(dir, LOCK_FILENAME);
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
  fs.writeFileSync(
    filePath,
    `# Auto-generated by skaile. Do not edit manually.\n${yamlStringify(lock, { indent: 2 })}`,
    "utf-8",
  );
}

/**
 * Create an empty lock file structure.
 *
 * @docLink packages/library/api-reference#lock-file
 */
export function createEmptyLockFile(): LockFile {
  return {
    version: 2,
    generated_at: new Date().toISOString(),
    assignments: [],
    requires_graph: [],
    presets_expanded: [],
  };
}

// ---------------------------------------------------------------------------
// Version detection
// ---------------------------------------------------------------------------

/**
 * Detect the workspace config version from a skaile.yaml file.
 * Returns 1 for v1 (no version field), 2 for v2, null if no file.
 *
 * @docLink packages/library/api-reference#workspace-config
 */
export function detectWorkspaceVersion(dir: string, filename = "skaile.yaml"): 1 | 2 | null {
  const filePath = path.join(dir, filename);
  if (!fs.existsSync(filePath)) return null;
  const content = fs.readFileSync(filePath, "utf-8");
  const raw = yamlParse(content) as Record<string, unknown>;
  if (!raw || typeof raw !== "object") return null;
  return raw.version === 2 ? 2 : 1;
}
